import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { getApiBaseUrl } from '../../config/runtime';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  ToggleButtonGroup,
  ToggleButton,
  Card,
  CardContent,
  CardActions,
  Stack,
  useTheme,
  useMediaQuery,
} from '@mui/material';

interface BusinessJoinRequest {
  id: string;
  businessId: string;
  businessName: string;
  businessABN: string;
  userId: string;
  userEmail: string;
  userFullName: string;
  requestType: string;
  status: string;
  requestedDate: string;
  reviewedDate: string | null;
  reviewedByUserName: string | null;
  rejectionReason: string | null;
}

const statusColor = (status: string): 'warning' | 'success' | 'error' | 'default' => {
  switch (status) {
    case 'Pending': return 'warning';
    case 'Approved': return 'success';
    case 'Rejected': return 'error';
    default: return 'default';
  }
};

const BusinessRequestsManagement: React.FC = () => {
  const auth = useAuth();
  const apiUrl = getApiBaseUrl();
  const authHeader = { Authorization: `Bearer ${auth.tokens?.accessToken}` };
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [requests, setRequests] = useState<BusinessJoinRequest[]>([]);
  const [filter, setFilter] = useState<'Pending' | 'All'>('Pending');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actioningId, setActioningId] = useState<string | null>(null);

  const [rejectTarget, setRejectTarget] = useState<BusinessJoinRequest | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');

  const fetchRequests = async (statusFilter: 'Pending' | 'All') => {
    setLoading(true);
    try {
      const url = statusFilter === 'Pending'
        ? `${apiUrl}/api/business-join-requests?status=Pending`
        : `${apiUrl}/api/business-join-requests`;
      const response = await fetch(url, { headers: authHeader });
      if (!response.ok) throw new Error('Failed to fetch requests');
      const data = await response.json();
      setRequests(Array.isArray(data) ? data : []);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (auth.isSignedIn) fetchRequests(filter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.isSignedIn, filter]);

  const handleApprove = async (request: BusinessJoinRequest) => {
    setActioningId(request.id);
    try {
      const response = await fetch(`${apiUrl}/api/business-join-requests/${request.id}/approve`, {
        method: 'POST',
        headers: authHeader,
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to approve request');
      }
      await fetchRequests(filter);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setActioningId(null);
    }
  };

  const handleReject = async () => {
    if (!rejectTarget) return;
    setActioningId(rejectTarget.id);
    try {
      const response = await fetch(`${apiUrl}/api/business-join-requests/${rejectTarget.id}/reject`, {
        method: 'POST',
        headers: { ...authHeader, 'Content-Type': 'application/json' },
        body: JSON.stringify({ rejectionReason: rejectionReason || undefined }),
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to reject request');
      }
      setRejectTarget(null);
      setRejectionReason('');
      await fetchRequests(filter);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setActioningId(null);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">Business Join Requests</Typography>
        <ToggleButtonGroup value={filter} exclusive size="small" onChange={(_, v) => v && setFilter(v)}>
          <ToggleButton value="Pending">Pending</ToggleButton>
          <ToggleButton value="All">All</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {requests.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">No requests found</Typography>
        </Paper>
      ) : isMobile ? (
        <Stack spacing={1.5}>
          {requests.map((req) => (
            <Card key={req.id} variant="outlined">
              <CardContent sx={{ pb: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                  <Typography variant="subtitle2" fontWeight={600}>{req.businessName}</Typography>
                  <Chip label={req.status} size="small" color={statusColor(req.status)} />
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace', display: 'block' }}>
                  ABN {req.businessABN}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                  <Chip label={req.requestType} size="small" variant="outlined" />
                  <Typography variant="body2">{req.userFullName}</Typography>
                </Box>
                <Typography variant="caption" color="text.secondary">{req.userEmail}</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  Requested {new Date(req.requestedDate).toLocaleDateString()}
                </Typography>
                {req.status === 'Rejected' && req.rejectionReason && (
                  <Typography variant="caption" color="text.secondary" display="block">
                    {req.rejectionReason}
                  </Typography>
                )}
              </CardContent>
              {req.status === 'Pending' && (
                <CardActions>
                  <Button
                    size="small"
                    variant="contained"
                    disabled={actioningId === req.id}
                    onClick={() => handleApprove(req)}
                  >
                    Approve
                  </Button>
                  <Button
                    size="small"
                    color="error"
                    disabled={actioningId === req.id}
                    onClick={() => setRejectTarget(req)}
                  >
                    Reject
                  </Button>
                </CardActions>
              )}
            </Card>
          ))}
        </Stack>
      ) : (
        <TableContainer component={Paper}>
          <Table sx={{ '& .MuiTableCell-root': { py: 1 } }}>
            <TableHead>
              <TableRow>
                <TableCell>Business</TableCell>
                <TableCell>ABN</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Requested By</TableCell>
                <TableCell>Requested</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {requests.map((req) => (
                <TableRow key={req.id}>
                  <TableCell>{req.businessName}</TableCell>
                  <TableCell sx={{ fontFamily: 'monospace' }}>{req.businessABN}</TableCell>
                  <TableCell>
                    <Chip label={req.requestType} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{req.userFullName}</Typography>
                    <Typography variant="caption" color="text.secondary">{req.userEmail}</Typography>
                  </TableCell>
                  <TableCell>{new Date(req.requestedDate).toLocaleDateString()}</TableCell>
                  <TableCell>
                    <Chip label={req.status} size="small" color={statusColor(req.status)} />
                    {req.status === 'Rejected' && req.rejectionReason && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        {req.rejectionReason}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    {req.status === 'Pending' && (
                      <>
                        <Button
                          size="small"
                          variant="contained"
                          disabled={actioningId === req.id}
                          onClick={() => handleApprove(req)}
                          sx={{ mr: 1 }}
                        >
                          Approve
                        </Button>
                        <Button
                          size="small"
                          color="error"
                          disabled={actioningId === req.id}
                          onClick={() => setRejectTarget(req)}
                        >
                          Reject
                        </Button>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={!!rejectTarget} onClose={() => setRejectTarget(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Reject Request — {rejectTarget?.businessName}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            minRows={2}
            label="Rejection Reason (optional)"
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            sx={{ mt: 1 }}
            inputProps={{ maxLength: 1000 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectTarget(null)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleReject}
            disabled={actioningId === rejectTarget?.id}
          >
            Reject Request
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BusinessRequestsManagement;
